import { useEffect, useRef } from 'react'
import { appToast } from '../../../components/AppToast'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import AppRichTextEditor from '../../../components/AppRichTextEditor'
import { useReportCardSettingsScreen } from './hooks/useReportCardSettingsScreen'

// Exact 15 Header tags from legacy TestReportCardSetting.aspx & Image 4
const HEADER_TAGS = [
  'InstituteLogo',
  'InstituteName',
  'InstituteShortName',
  'Address_Line1',
  'Address_Line2',
  'Address_Line3',
  'Email',
  'WebSite',
  'InstitueMobile',
  'StudentPhoto',
  'Name',
  'RollNo',
  'IDCardNo',
  'Course',
  'Batch',
]

// Exact 3 Footer tags from legacy TestReportCardSetting.aspx & Image 4
const FOOTER_TAGS = [
  'InstituteName',
  'PrintDate',
  'AttendanceSummary',
]

// Exact 10 Body checkboxes from legacy TestReportCardSetting.aspx & Image 4
const BODY_FIELDS = [
  { key: 'testNo', label: 'TestNo' },
  { key: 'subject', label: 'Subject' },
  { key: 'testDate', label: 'Test Date' },
  { key: 'marks', label: 'Marks' },
  { key: 'outOf', label: 'Out Of' },
  { key: 'percent', label: 'Percent(%)' },
  { key: 'highest', label: 'Highest' },
  { key: 'grade', label: 'Grade' },
  { key: 'remark', label: 'Remark' },
  { key: 'topic', label: 'Topic' },
]

// 5-tier grading system matching Image 4
const GRADING_TIERS = [
  { key: 'A', label: 'A>', minField: 'gradeA', nameField: 'gradeAName', placeholder: 'e.g. Excellent' },
  { key: 'B', label: 'B>', minField: 'gradeB', nameField: 'gradeBName', placeholder: 'e.g. Good' },
  { key: 'C', label: 'C>', minField: 'gradeC', nameField: 'gradeCName', placeholder: 'e.g. Satisfactory' },
  { key: 'D', label: 'D>', minField: 'gradeD', nameField: 'gradeDName', placeholder: 'e.g. Pass' },
  { key: 'E', label: 'E>', minField: 'gradeE', nameField: 'gradeEName', placeholder: 'e.g. Needs Improvement' },
]

export default function ReportCardSettingsScreen() {
  const { state, actions } = useReportCardSettingsScreen()
  const headerEditorRef = useRef(null)
  const footerEditorRef = useRef(null)

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const insertTagIntoEditor = (editorType, tagName) => {
    const token = `#${tagName}#`
    if (editorType === 'header') {
      headerEditorRef.current?.insertTag(token)
    } else {
      footerEditorRef.current?.insertTag(token)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-24">
      {/* ── Page Header (Rule 87: Canopy Hierarchy - No arrow_back on top-level submodules) ── */}
      <section className="flex flex-col gap-1 border-b border-outline-variant/10 pb-5">
        <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
          Report Card Settings
        </h1>
        <p className="mt-1 text-xs font-medium text-on-surface-variant">
          Configure customizable header and footer templates, score columns, and automated grading brackets for student progress reports.
        </p>
      </section>

      {state.isLoading ? (
        <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center text-sm text-on-surface-variant font-medium">
          Loading report card template settings...
        </div>
      ) : (
        <div className="flex flex-col gap-6">

          {/* ── Header Section (Matching Image 4) ── */}
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Left Label */}
              <div className="lg:col-span-1 pt-1">
                <label className="text-sm font-bold text-on-surface">Header</label>
              </div>

              {/* Center WYSIWYG Editor */}
              <div className="lg:col-span-7 flex flex-col">
                <AppRichTextEditor
                  ref={headerEditorRef}
                  value={state.formData.header || ''}
                  onChange={(val) => actions.handleChange({ target: { name: 'header', value: val } })}
                  height={190}
                  placeholder="Type header content or click tags on the right to insert dynamic student tokens..."
                />
              </div>

              {/* Right TIPS Box (Matching Image 4) */}
              <div className="lg:col-span-4 rounded-xl border border-outline-variant/30 bg-surface-container-low/50 p-4 text-xs text-on-surface-variant leading-relaxed shadow-xs">
                <h5 className="font-bold uppercase tracking-wider text-on-surface text-[11px] mb-1.5">TIPS:</h5>
                <ul className="list-disc pl-4 space-y-2">
                  <li>
                    Type desired text along with fields listed below. Fields are automatically replaced with relevant student information.
                  </li>
                  <li>
                    <span className="font-semibold text-on-surface">Header:</span>{' '}
                    <span className="inline-flex flex-wrap gap-x-2 gap-y-1.5 mt-1.5">
                      {HEADER_TAGS.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => insertTagIntoEditor('header', tag)}
                          className="rounded-md bg-surface-container px-2 py-0.5 text-primary hover:bg-primary hover:text-on-primary transition-all font-semibold cursor-pointer text-xs"
                          title={`Insert #${tag}# into Header`}
                        >
                          {tag}
                        </button>
                      ))}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* ── Body Columns Row (Matching Image 4) ── */}
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-1">
                <label className="text-sm font-bold text-on-surface">Body</label>
              </div>
              <div className="lg:col-span-11 flex flex-wrap items-center gap-x-6 gap-y-3">
                {BODY_FIELDS.map((field) => {
                  const isChecked = Boolean(state.formData.bodyFields?.[field.key])
                  return (
                    <label
                      key={field.key}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-on-surface cursor-pointer select-none hover:text-primary transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => actions.handleBodyFieldChange(field.key)}
                        className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 cursor-pointer"
                      />
                      <span>{field.label}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>

          {/* ── Footer Section (Matching Image 4) ── */}
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Left Label */}
              <div className="lg:col-span-1 pt-1">
                <label className="text-sm font-bold text-on-surface">Footer</label>
              </div>

              {/* Center WYSIWYG Editor */}
              <div className="lg:col-span-7 flex flex-col">
                <AppRichTextEditor
                  ref={footerEditorRef}
                  value={state.formData.footer || ''}
                  onChange={(val) => actions.handleChange({ target: { name: 'footer', value: val } })}
                  height={150}
                  placeholder="Type footer content or click tags on the right to insert dynamic footer notes..."
                />
              </div>

              {/* Right TIPS Box (Matching Image 4) */}
              <div className="lg:col-span-4 rounded-xl border border-outline-variant/30 bg-surface-container-low/50 p-4 text-xs text-on-surface-variant leading-relaxed shadow-xs">
                <h5 className="font-bold uppercase tracking-wider text-on-surface text-[11px] mb-1.5">TIPS:</h5>
                <ul className="list-disc pl-4 space-y-2">
                  <li>
                    Type desired text along with fields listed below. Fields are automatically replaced with relevant summary text.
                  </li>
                  <li>
                    <span className="font-semibold text-on-surface">Footer:</span>{' '}
                    <span className="inline-flex flex-wrap gap-x-2 gap-y-1.5 mt-1.5">
                      {FOOTER_TAGS.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => insertTagIntoEditor('footer', tag)}
                          className="rounded-md bg-surface-container px-2 py-0.5 text-primary hover:bg-primary hover:text-on-primary transition-all font-semibold cursor-pointer text-xs"
                          title={`Insert #${tag}# into Footer`}
                        >
                          {tag}
                        </button>
                      ))}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* ── Absentee Marks Include Row (Matching Image 4) ── */}
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-6 py-4 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-1" />
              <div className="lg:col-span-11">
                <label className="inline-flex items-center gap-2.5 text-xs font-bold text-on-surface cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="absenteeMarksIncludeExclude"
                    checked={Boolean(state.formData.absenteeMarksIncludeExclude)}
                    onChange={actions.handleChange}
                    className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 cursor-pointer"
                  />
                  <span>Absentee Marks Include</span>
                </label>
              </div>
            </div>
          </div>

          {/* ── GRADING SYSTEM (Matching Image 4) ── */}
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
            <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface mb-4">
              GRADING SYSTEM:
            </h4>

            <div className="flex flex-col gap-3 max-w-[850px]">
              {GRADING_TIERS.map((tier) => (
                <div key={tier.key} className="grid grid-cols-[40px_120px_30px_60px_1fr] items-center gap-3">
                  {/* Tier Label (A>, B>, etc.) */}
                  <span className="text-xs font-bold text-on-surface text-right pr-1">
                    {tier.label}
                  </span>

                  {/* Percentage Input */}
                  <input
                    type="number"
                    name={tier.minField}
                    value={state.formData[tier.minField] ?? ''}
                    onChange={actions.handleGradeChange}
                    min="0"
                    max="100"
                    placeholder="0"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3 py-2 text-xs font-semibold text-on-surface focus:border-primary focus:outline-none"
                  />

                  {/* % Label */}
                  <span className="text-xs font-medium text-on-surface-variant">
                    %
                  </span>

                  {/* Grade Label */}
                  <span className="text-xs font-semibold text-on-surface text-right pr-2">
                    Grade
                  </span>

                  {/* Grade Nomenclature text input */}
                  <input
                    type="text"
                    name={tier.nameField}
                    value={state.formData[tier.nameField] || ''}
                    onChange={actions.handleChange}
                    maxLength={50}
                    placeholder={tier.placeholder}
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface px-3.5 py-2 text-xs text-on-surface placeholder:text-on-surface-variant/40 focus:border-primary focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>

          {state.error && (
            <div className="rounded-2xl border border-error/30 bg-error-container p-4 text-xs font-medium text-error">
              {state.error}
            </div>
          )}
        </div>
      )}

      {/* ── Standard Save Bar (Submit button matching Image 4) ── */}
      <AppFormFooterBar
        onSaveExit={actions.handleSave}
        saveLabel="Submit"
        isSaving={state.isSaving}
      />
    </div>
  )
}
